import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Badge, Card, Form, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type ContactMessage = {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  message: string;
  createdAt?: string;
};

type ContactMessagesResponse = {
  messages: ContactMessage[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

const ContactMessages = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchMessages = useCallback(async () => {
    if (!token) {
      setError('Authentication is required.');
      setRows([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get<ContactMessagesResponse>('/contact-messages', {
        params: { page, limit },
        headers: { Authorization: `Bearer ${token}` },
      });

      const payload = response.data;
      setRows(Array.isArray(payload?.messages) ? payload.messages : []);
      setTotal(Number(payload?.total ?? 0));
      setTotalPages(Number(payload?.totalPages ?? 0));
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Failed to load contact messages.');
      setRows([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [limit, page, token]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return (
    <Card className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Contact Messages</h4>
        <Badge bg="dark">{total}</Badge>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <div className="d-flex justify-content-end mb-3">
        <Form.Select
          value={limit}
          onChange={(event) => {
            setPage(1);
            setLimit(Number(event.target.value));
          }}
          style={{ maxWidth: '140px' }}
          aria-label="Rows per page"
        >
          <option value={10}>10 / page</option>
          <option value={20}>20 / page</option>
          <option value={50}>50 / page</option>
        </Form.Select>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Message</th>
            <th>Received</th>
          </tr>
        </thead>
        <tbody>
          {!loading && rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center">No messages found.</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row._id}>
                <td>{row.name}</td>
                <td>{row.email}</td>
                <td>{row.phone || '-'}</td>
                <td style={{ whiteSpace: 'pre-wrap', minWidth: 260 }}>{row.message}</td>
                <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center">
        <small>Showing {rows.length} of {total} messages</small>
        <div className="d-flex gap-2 align-items-center">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page <= 1}
          >
            Prev
          </button>
          <span>Page {page}{totalPages > 0 ? ` of ${totalPages}` : ''}</span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={totalPages > 0 ? page >= totalPages : rows.length < limit}
          >
            Next
          </button>
        </div>
      </div>
    </Card>
  );
};

export default ContactMessages;
